import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";

/*
  위반 상세 패널 아래 붙는 지도 — «그 사람이 어디 있나».

  위치는 /api/live-sources 의 latitude/longitude 다(지도 메뉴와 같은 출처).
  송출 중이 아니면 그리지 않는다 — 꺼진 카메라의 마지막 좌표를 현재처럼 찍으면
  사람을 엉뚱한 데서 찾는다.

  안 나올 때 «왜» 안 나오는지 반드시 적는다. 빈 칸만 보이면 고장인지 정상인지
  구분이 안 돼서 «김철수는 왜 지도가 안 나오지?» 같은 질문이 반복된다.
    · 카메라를 못 찾음      → 등록/권한 문제
    · 송출 중이 아님        → 정상(위치를 보내지 않는다)
    · 송출 중인데 좌표 없음 → 앱이 GPS를 아직 못 잡음(실내·권한 거부)

  조작은 막아둔다. 패널 안에서 지도가 끌려다니면 오작동처럼 느껴진다.
*/

const SOURCE_API = "/api/live-sources";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickLatLng(item) {
  const lat = [item?.latitude, item?.Latitude, item?.GpsLat, item?.gpsLat, item?.lat]
    .map(num)
    .find((v) => v != null);
  const lng = [item?.longitude, item?.Longitude, item?.GpsLng, item?.gpsLng, item?.lng]
    .map(num)
    .find((v) => v != null);

  if (lat == null || lng == null) return null;
  if (lat === 0 && lng === 0) return null; // 좌표 미수신 기본값
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lat, lng];
}

function fmtAgo(iso) {
  const t = Date.parse(String(iso || "").replace(" ", "T"));
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  return `${Math.floor(min / 60)}시간 전`;
}

export default function MiniMap({ streamKey, onOpenMap }) {
  const [state, setState] = useState({ pos: null, reason: "loading", at: "" });
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const key = useMemo(() => String(streamKey || "").trim(), [streamKey]);

  useEffect(() => {
    if (!key) {
      setState({ pos: null, reason: "nokey", at: "" });
      return undefined;
    }
    let dead = false;

    const load = async () => {
      try {
        const res = await fetch(SOURCE_API, { credentials: "include", cache: "no-store" });
        const raw = res.ok ? await res.json() : null;
        const arr = Array.isArray(raw?.sources) ? raw.sources : Array.isArray(raw) ? raw : [];

        const hit = arr.find((c) => String(c?.streamKey || "").trim() === key);
        if (dead) return;

        if (!hit) {
          setState({ pos: null, reason: "nocam", at: "" });
          return;
        }
        if (String(hit?.liveStatus || "").toUpperCase() !== "ONLINE") {
          setState({ pos: null, reason: "offline", at: "" });
          return;
        }

        const pos = pickLatLng(hit);
        setState({
          pos,
          reason: pos ? "ok" : "nogps",
          at: fmtAgo(hit?.locationUpdatedAt),
        });
      } catch {
        if (!dead) setState({ pos: null, reason: "error", at: "" });
      }
    };

    load();
    const t = setInterval(load, 20000);
    return () => {
      dead = true;
      clearInterval(t);
    };
  }, [key]);

  const { pos, reason, at } = state;

  useEffect(() => {
    if (!pos || !boxRef.current) return undefined;

    if (!mapRef.current) {
      mapRef.current = L.map(boxRef.current, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: false,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(
        mapRef.current
      );
    }

    mapRef.current.setView(pos, 17);

    if (markerRef.current) {
      markerRef.current.setLatLng(pos);
    } else {
      markerRef.current = L.circleMarker(pos, {
        radius: 8,
        color: "#ffffff",
        weight: 3,
        fillColor: "#d92d20",
        fillOpacity: 1,
      }).addTo(mapRef.current);
    }

    // 패널 크기가 잡힌 뒤 그려야 타일이 잘려 보이지 않는다.
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 60);

    // 창 크기가 바뀌면 타일이 어긋난다.
    const ro = new ResizeObserver(() => mapRef.current?.invalidateSize());
    ro.observe(boxRef.current);

    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, [pos]);

  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    },
    []
  );

  const message =
    reason === "loading"
      ? "위치 확인 중…"
      : reason === "offline"
      ? "송출 중이 아닙니다"
      : reason === "nogps"
      ? "GPS 신호를 아직 못 받았습니다"
      : reason === "nocam"
      ? "카메라 정보를 찾지 못했습니다"
      : "위치를 불러오지 못했습니다";

  return (
    <div className="mt-3 flex min-h-0 flex-1 flex-col">
      <div className="mb-1.5 flex items-center">
        <span className="text-[11.5px] font-medium text-slate-500 dark:text-slate-400">
          현재 위치
          {pos && at && <span className="ml-1.5 text-slate-400">· {at}</span>}
        </span>
        {pos && (
          <button
            type="button"
            onClick={onOpenMap}
            className="ml-auto text-[11.5px] font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          >
            지도에서 보기 ›
          </button>
        )}
      </div>

      {pos ? (
        <div
          ref={boxRef}
          className="min-h-[120px] w-full flex-1 overflow-hidden rounded-lg border border-slate-200 dark:border-white/10"
        />
      ) : (
        <div className="flex min-h-[120px] w-full flex-1 items-center justify-center rounded-lg border border-dashed border-slate-200 px-3 text-center text-[11.5px] text-slate-400 dark:border-white/10">
          {message}
        </div>
      )}
    </div>
  );
}
